/* eslint-disable import/no-extraneous-dependencies */
import Slider from 'react-slick';
import { useRef, useEffect, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Stack, Typography, Avatar, Box, Container } from '@mui/material';
import cssStyles from 'src/utils/cssStyles';
import BgOverlay from 'src/components/BgOverlay';
import Image from 'src/components/Image';
import { useLocales } from 'src/locales';

// ----------------------------------------------------------------------

export default function LandingHero() {
  const theme = useTheme();

  const { t } = useLocales();

  const [selected, setSelected] = useState(0);

  const [carouselContent, setCarouselContent] = useState<Slider>();

  const [carouselThumbnail, setCarouselThumbnail] = useState<Slider>();

  const carouselRef1 = useRef<Slider | null>(null);

  const carouselRef2 = useRef<Slider | null>(null);

  useEffect(() => {
    setCarouselContent(carouselRef1.current || undefined);
    setCarouselThumbnail(carouselRef2.current || undefined);
  }, [selected]);

  const landingItems = [
    {
      id: '1',
      slug: t('future_begins'),
      title: 'WebSummit',
      price: 1500,
      heroImg: '/assets/summit.jpg',
      ratings: 4.7,
      reviews: 124,
      coverImg: 'https://techukraine.org/wp-content/uploads/2023/08/IMG_1228.jpeg',
      location: 'Lisbon',
      duration: 'Thursday, November 14',
      continent: 'Europe',
      priceSale: 1300,
      description: t('web_summit_desc'),
    },
    {
      id: '2',
      slug: t('squad_service'),
      title: 'Pathen',
      price: 1500,
      heroImg: '/assets/pathen_banner.png',
      ratings: 4.7,
      reviews: 124,
      coverImg: '/assets/pathen.svg',
      location: 'Hamburg, Germany',
      duration: '',
      continent: 'Europe',
      priceSale: 1300,
      description: t('freelancers_desc'),
    },
  ];

  const carouselThumbnailSettings = {
    centerMode: true,
    centerPadding: '0px',
    arrows: false,
    vertical: true,
    infinite: true,
    autoplay: true,
    slidesToShow: 2,
    slidesToScroll: 1,
    verticalSwiping: true,
    autoplaySpeed: 10000,
    swipeToSlide: true,
    focusOnSelect: true,
    rtl: Boolean(theme.direction === 'rtl'),
    beforeChange: (current: number, next: number) => setSelected(next),
  };

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative' }}>
      {!!landingItems.length && (
        <Slider
          {...{
            initialSlide: selected,
            dots: false,
            speed: 1440,
            arrows: false,
            slidesToShow: 1,
            draggable: false,
            slidesToScroll: 1,
            adaptiveHeight: true,
          }}
          asNavFor={carouselThumbnail}
          ref={carouselRef1}
        >
          {landingItems.map((tour, index) => (
            <ContentItem key={tour.id} tour={tour} isSelected={selected === index} />
          ))}
        </Slider>
      )}

      <Stack
        spacing={2}
        justifyContent="center"
        sx={{
          top: 0,
          height: 1,
          maxWidth: 220,
          position: 'absolute',
          display: { xs: 'none', md: 'flex' },
          right: { xs: 20, lg: '6%', xl: '10%' },
        }}
      >
        {!!landingItems.length && (
          <Slider {...carouselThumbnailSettings} asNavFor={carouselContent} ref={carouselRef2}>
            {landingItems.map((tour, index) => (
              <ThumbnailItem key={tour.id} tour={tour} isSelected={selected === index} />
            ))}
          </Slider>
        )}
      </Stack>
    </Box>
  );
}

// ----------------------------------------------------------------------

type ItemProps = {
  tour: any;
  isSelected?: boolean;
};

function ContentItem({ tour, isSelected }: ItemProps) {
  const theme = useTheme();
  const { slug, heroImg, duration, description } = tour;

  return (
    <Box
      sx={{
        display: 'flex',
        textAlign: 'center',
        alignItems: 'center',
        position: 'relative',
        color: 'common.white',
        justifyContent: 'center',
      }}
    >
      {heroImg === '/assets/summit.jpg' && (
        <Stack
          sx={{
            left: { md: 10, xs: 10 },
            right: 0,
            top: { md: 10, xs: 10 },
            position: { xs: 'absolute', md: 'absolute' },
            mx: 'auto',
            zIndex: 10,
          }}
        >
          <a
            href={`${window.location.origin}/assets/venue.pdf`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: { xs: 250, md: 450 },
                height: { xs: 110, md: 190 },
                '&:before, &:after': {
                  content: '""',
                  position: 'absolute',
                  left: -2,
                  top: -2,
                  width: 'calc(100% + 4px)',
                  height: 'calc(100% + 4px)',
                  borderRadius: 1,
                  background:
                    'linear-gradient(45deg, #52F4CA, #0E2A40, #52F4CA, #ffff00, #52F4CA, #0E2A40, #52F4CA, #0E2A40, #52F4CA, #0E2A40)',
                  backgroundSize: '200%',
                  zIndex: -1,
                  animation: 'steam 20s linear infinite',
                },
                '&:after': {
                  filter: 'blur(10px)',
                },
                '@keyframes steam': {
                  '0%': { backgroundPosition: '0 0' },
                  '50%': { backgroundPosition: '400% 0' },
                  '100%': { backgroundPosition: '0 0' },
                },
              }}
            >
              <Image
                alt="hero"
                src="/assets/Stand-A240.png"
                sx={{
                  height: { xs: 110, md: 190 },
                  width: { xs: 250, md: 450 },
                  borderRadius: 1,
                }}
              />
            </Box>
          </a>
        </Stack>
      )}
      {/* Content */}
      <Stack
        alignItems="center"
        sx={{
          zIndex: 9,
          py: { xs: 20, md: 0 },
          position: { md: 'absolute' },
        }}
      >
        {/* <Typography variant="overline" sx={{ color: 'primary.main' }}>
          {location}
        </Typography> */}
        {duration && (
          <Typography
            variant="subtitle1"
            sx={{
              color: 'primary.main',
              mb: 5,
              bgcolor: alpha('#0E2A40', 0.6),
              p: 1,
              borderRadius: 1,
            }}
          >
            {duration}
          </Typography>
        )}

        <Typography variant="h1" sx={{ maxWidth: 480 }}>
          {slug}
        </Typography>

        <Stack
          alignItems="center"
          typography="subtitle1"
          spacing={{ xs: 2.5, md: 5 }}
          direction={{ xs: 'column', md: 'row' }}
          sx={{
            my: 5,
            color: 'primary.main',
            '& svg': {
              mr: 1,
              width: 24,
              height: 24,
              color: 'primary.main',
            },
          }}
        >
          {description}
        </Stack>
      </Stack>

      {/* Background */}
      <Box
        sx={{
          width: 1,
          height: 1,
          position: {
            xs: 'absolute',
            md: 'relative',
          },
        }}
      >
        <BgOverlay
          startColor={alpha(theme.palette.grey[900], 0.48)}
          endColor={alpha(theme.palette.grey[900], 0.48)}
        />
        <BgOverlay />
        <Image
          alt="hero"
          src={heroImg}
          sx={{
            height: { xs: 1, md: '100vh' },
          }}
        />
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------------

function ThumbnailItem({ tour, isSelected }: ItemProps) {
  const theme = useTheme();
  const { title, coverImg, location } = tour;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2.5}
      sx={{
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        color: 'common.white',
        ...(isSelected && {
          borderRadius: 2,
          ...cssStyles().bgBlur({
            blur: 8,
            opacity: 0.08,
            color: theme.palette.common.white,
          }),
        }),
      }}
    >
      <Avatar src={coverImg} sx={{ width: 48, height: 48 }} />
      <Stack spacing={0.5}>
        <Typography variant="overline" sx={{ color: 'primary.main' }}>
          {location}
        </Typography>

        {title}
      </Stack>
    </Stack>
  );
}
